import { campaignSummaries, catalogGames, demoCampaign, demoSession, libraryAssets } from "./demo";
import { emptyCampaignDefinition } from "./creator";
import { EMPTY_VENICE_CONNECTION } from "./venice";
import type { AiTurnDiagnostics, Campaign, CampaignDraftDefinition, CampaignSummary, CatalogGame, CharacterDefinition, CharacterDraft, CoreStats, CreationVisibility, CreatorAiQuote, CreatorAiRequest, CreatorAiRunResult, DialectAttempt, DialectMentorTone, DialectPracticeMode, DialectSession, DialectState, GameDraft, ImageAspectRatio, ImageJob, ImageOutputFormat, ImageQuote, ImageResolution, LibraryAsset, MapModel, MediaAsset, PlayerPersona, Session, StoryMessage, TimelineEvent, TurnChange, VeniceConnection, VeniceConnectionStatus } from "../types";

const API_ROOT = import.meta.env.VITE_API_ROOT ?? "/api/v1";
const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";
const DEMO_SESSION_KEY = "tfgames.demo.session";
let activeCsrfToken: string | null = null;
type RequestOptions = RequestInit & { csrfToken?: string };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> | null = null,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function apiErrorMessage(code: string | undefined, status: number): string {
  switch (code) {
    case "STALE_WORLD_VERSION": return "The world changed before this action was applied. Reload and try again.";
    case "TURN_IN_PROGRESS": return "The world is already resolving another action.";
    case "CHECK_PENDING": return "Resolve the pending check before taking another action.";
    case "TURN_NOT_REGENERATABLE": return "Only a completed AI response can be redone.";
    case "TURN_NOT_LATEST": return "Only the latest AI response can be redone.";
    case "TURN_NOT_IN_ACTIVE_BRANCH": return "That response is no longer on the current story path. Reload and try again.";
    case "TURN_SNAPSHOT_NOT_FOUND": return "The saved state before that response is unavailable.";
    case "CHARACTER_IN_USE": return "This character is still used by a campaign draft. Remove it from that campaign before deleting it.";
    case "QUOTA_EXCEEDED": return "Your monthly token allowance has been reached.";
    case "ACTION_RATE_EXCEEDED": return "The action limit has been reached. Try again later.";
    case "GLOBAL_BUDGET_EXCEEDED": return "AI generation is temporarily paused.";
    case "VENICE_CONNECTION_REQUIRED": return "Connect your Venice account to use this AI feature.";
    case "VENICE_KEY_INVALID": return "Venice did not accept that API key. Check it and try again.";
    case "VENICE_RECONNECT_REQUIRED": return "Your Venice key expired or was revoked. Reconnect it in your profile.";
    case "VENICE_FUNDS_REQUIRED": return "Your Venice account needs more Credits for this request.";
    case "VENICE_KEY_LIMIT_REACHED": return "This Venice key reached its spending limit. Adjust it in Venice or try again after it resets.";
    case "VENICE_RATE_LIMITED":
    case "VENICE_429": return "Venice is receiving too many requests from this key. Try again shortly.";
    case "VENICE_UNAVAILABLE":
    case "VENICE_NETWORK_ERROR": return "Venice is temporarily unavailable. Your work has been kept.";
    case "VENICE_TIMEOUT": return "The AI took too long to answer. Please try again.";
    case "MODEL_REPAIR_FAILED":
    case "MODEL_RESPONSE_INVALID": return "The AI did not return a complete valid response. No story or world changes were applied.";
    default:
      if (code?.startsWith("VENICE_") === true) return "The AI service could not complete this turn. Please try again.";
      return code ?? `Request failed (${status})`;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body && !(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  const method = (options.method ?? "GET").toUpperCase();
  const csrf = options.csrfToken ?? activeCsrfToken;
  if (method !== "GET" && method !== "HEAD") {
    if (csrf) headers.set("X-CSRF-Token", csrf);
    if (!headers.has("Idempotency-Key")) headers.set("Idempotency-Key", crypto.randomUUID());
  }
  const response = await fetch(`${API_ROOT}${path}`, { ...options, headers, credentials: "include" });
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string; message?: string; details?: Record<string, unknown>; issues?: unknown[] } | null;
    const code = payload?.error ?? `HTTP_${response.status}`;
    const retryAfter = Number(response.headers.get("Retry-After"));
    const issueMessage = Array.isArray(payload?.issues)
      ? payload.issues.map((issue) => typeof issue === "string" ? issue : JSON.stringify(issue)).filter(Boolean).join(" · ")
      : "";
    throw new ApiError(
      response.status,
      code,
      payload?.message ?? (issueMessage || apiErrorMessage(code, response.status)),
      payload?.details ?? (payload?.issues === undefined ? null : { issues: payload.issues }),
      Number.isFinite(retryAfter) ? retryAfter : null,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

function demoSignedIn(): boolean { return window.localStorage.getItem(DEMO_SESSION_KEY) === "true"; }
function setDemoSignedIn(value: boolean): void { value ? window.localStorage.setItem(DEMO_SESSION_KEY, "true") : window.localStorage.removeItem(DEMO_SESSION_KEY); }
function run<T>(live: () => Promise<T>, demo: () => T | Promise<T>): Promise<T> { return DEMO_MODE ? Promise.resolve(demo()) : live(); }

let demoVenice: VeniceConnection = {
  ...EMPTY_VENICE_CONNECTION,
  connected: true,
  status: "ready",
  accessPermitted: true,
  keySuffix: "demo",
  connectedAt: new Date().toISOString(),
  lastCheckedAt: new Date().toISOString(),
  balance: { spendableCredits: 541, bundledCredits: 141, usdCredits: 300, diemCredits: 100 },
  capabilities: { text: true, imageGeneration: true, imageEditing: true },
};
let demoMedia: MediaAsset[] = [];
const demoImageJobs = new Map<string, ImageJob>();
const demoCharacterDrafts = new Map<string, CharacterDraft>();
const demoGameDrafts = new Map<string, GameDraft>();

type BackendUser = { id: string; username: string; role: "admin" | "user"; adultVerificationStatus: "unverified" | "verified" };
type BackendUsage = Session["quota"] & { events?: unknown[] };
type BackendSession = { user: BackendUser; csrfToken: string; usage?: BackendUsage; venice?: unknown };

const nullableNumber = (value: unknown): number | null => typeof value === "number" && Number.isFinite(value) ? value : null;

function creationEnvelope(value: unknown, preferred: "character" | "game"): Record<string, unknown> {
  const envelope = record(value);
  return record(envelope.draft ?? envelope[preferred] ?? value);
}

function normalizeVisibility(value: unknown): CreationVisibility {
  if (value === "public" || value === "unlisted" || value === "private") return value;
  return value === "published" ? "public" : "private";
}

function absoluteAppUrl(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null;
  if (typeof window === "undefined") return value;
  return new URL(value, window.location.origin).toString();
}

function normalizeCharacterDraft(value: unknown): CharacterDraft {
  const raw = creationEnvelope(value, "character");
  const definition = record(raw.definition);
  const stats = record(definition.stats);
  const now = new Date().toISOString();
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    ...(typeof raw.ownerId === "string" ? { ownerId: raw.ownerId } : {}),
    status: raw.status === "ready" ? "ready" : "draft",
    revision: Number.isInteger(raw.revision) ? Number(raw.revision) : 0,
    visibility: normalizeVisibility(raw.visibility),
    shareToken: typeof raw.shareToken === "string" ? raw.shareToken : null,
    shareUrl: absoluteAppUrl(raw.shareUrl),
    currentVersionId: typeof raw.currentVersionId === "string" ? raw.currentVersionId : null,
    definition: {
      name: String(definition.name ?? raw.title ?? "Untitled character"),
      gender: (["cis_man", "cis_woman", "trans_man", "trans_woman", "nonbinary", "other"] as const).includes(definition.gender as never) ? definition.gender as CharacterDefinition["gender"] : "nonbinary",
      adultAge: Number.isInteger(definition.adultAge) ? Number(definition.adultAge) : 25,
      pronouns: String(definition.pronouns ?? "they / them"),
      role: definition.role === "npc" ? "npc" : "player",
      appearance: String(definition.appearance ?? ""),
      personality: String(definition.personality ?? ""),
      personalityTraits: stringArray(definition.personalityTraits),
      background: String(definition.background ?? ""),
      speechStyle: String(definition.speechStyle ?? ""),
      motivations: String(definition.motivations ?? ""),
      likes: stringArray(definition.likes),
      dislikes: stringArray(definition.dislikes),
      privateNotes: String(definition.privateNotes ?? ""),
      aiInstructions: String(definition.aiInstructions ?? ""),
      boundaries: stringArray(definition.boundaries),
      stats: {
        STR: Number(stats.STR ?? 10), DEX: Number(stats.DEX ?? 10), CON: Number(stats.CON ?? 10), INT: Number(stats.INT ?? 10),
        WIS: Number(stats.WIS ?? 10), CHA: Number(stats.CHA ?? 10), FEM: Number(stats.FEM ?? 0), COR: Number(stats.COR ?? 0),
      },
      profileMediaId: typeof definition.profileMediaId === "string" ? definition.profileMediaId : null,
      publicDescription: String(definition.publicDescription ?? ""),
      tags: stringArray(definition.tags),
    },
    createdAt: String(raw.createdAt ?? now),
    updatedAt: String(raw.updatedAt ?? now),
  };
}

function normalizeGameDraft(value: unknown): GameDraft {
  const raw = creationEnvelope(value, "game");
  const definition = normalizeCampaignDefinition({ ...record(raw.definition), title: raw.title, summary: raw.summary });
  const now = new Date().toISOString();
  return {
    id: String(raw.id ?? crypto.randomUUID()),
    ...(typeof raw.ownerId === "string" ? { ownerId: raw.ownerId } : {}),
    status: raw.status === "ready" ? "ready" : "draft",
    revision: Number.isInteger(raw.revision) ? Number(raw.revision) : 0,
    visibility: normalizeVisibility(raw.visibility),
    shareToken: typeof raw.shareToken === "string" ? raw.shareToken : null,
    shareUrl: absoluteAppUrl(raw.shareUrl),
    currentVersionId: typeof raw.currentVersionId === "string" ? raw.currentVersionId : null,
    definition,
    createdAt: String(raw.createdAt ?? now),
    updatedAt: String(raw.updatedAt ?? now),
  };
}

function normalizeCampaignDefinition(value: unknown): CampaignDraftDefinition {
  const fallback = emptyCampaignDefinition();
  const source = record(value);
  const basics = record(source.basics);
  const themes = record(source.themes);
  const agency = record(source.agency);
  const world = record(source.world);
  const mechanics = record(source.mechanics);
  const publicInfo = record(source.publicInfo);
  const pacing = basics.pacing === "slow" || basics.pacing === "fast" ? basics.pacing : "moderate";
  const transformationAliases: Record<string, string> = { adult_age_regression: "age_regression", anthropomorphic_furry: "furry_anthropomorphic" };
  const adultAliases: Record<string, string> = { heterosexual_sex: "hetero_sex" };
  const allowedTransformationThemes = new Set(["male_to_female", "female_to_male", "sissification", "bimbofication", "body_swap", "age_regression", "shrinking", "animal", "furry_anthropomorphic", "demon", "robot", "inanimate_object", "mental_changes", "voluntary", "involuntary"]);
  const allowedAdultThemes = new Set(["hetero_sex", "lesbian_sex", "gay_sex", "group_sex", "submission", "domination", "pregnancy", "consensual_non_consent"]);
  const namedItems = (items: unknown): CampaignDraftDefinition["transformations"] => Array.isArray(items) ? items.flatMap((candidate, index) => {
    const item = record(candidate);
    if (!Object.keys(item).length) return [];
    return [{ id: String(item.id ?? `item_${index + 1}`), name: String(item.name ?? ""), description: String(item.description ?? "") }];
  }) : [];
  const maps: CampaignDraftDefinition["world"]["maps"] = Array.isArray(world.maps) ? world.maps.flatMap((candidate, index) => {
    const map = record(candidate);
    if (!Object.keys(map).length) return [];
    return [{
      id: String(map.id ?? `map_${index + 1}`),
      name: String(map.name ?? `Map ${index + 1}`),
      description: String(map.description ?? ""),
      moodMediaId: typeof map.moodMediaId === "string" ? map.moodMediaId : null,
      tileMapPrompt: String(map.tileMapPrompt ?? ""),
      width: Math.max(6, Math.min(50, Number.isInteger(map.width) ? Number(map.width) : 20)),
      height: Math.max(6, Math.min(50, Number.isInteger(map.height) ? Number(map.height) : 15)),
      tileMap: typeof map.tileMap === "object" && map.tileMap !== null && !Array.isArray(map.tileMap) ? map.tileMap as Record<string, unknown> : null,
    }];
  }) : [];
  const chapters: CampaignDraftDefinition["chapters"] = Array.isArray(source.chapters) ? source.chapters.flatMap((candidate, index) => {
    const chapter = record(candidate);
    if (!Object.keys(chapter).length) return [];
    return [{
      id: String(chapter.id ?? `chapter_${index + 1}`),
      name: String(chapter.name ?? `Chapter ${index + 1}`),
      prompt: String(chapter.prompt ?? ""),
      goal: String(chapter.goal ?? ""),
      targetTurns: Math.max(1, Number.isInteger(chapter.targetTurns) ? Number(chapter.targetTurns) : 10),
      nextChapterIds: stringArray(chapter.nextChapterIds ?? (typeof chapter.nextChapterId === "string" ? [chapter.nextChapterId] : [])),
      mapIds: stringArray(chapter.mapIds),
      characterIds: stringArray(chapter.characterIds),
    }];
  }) : [];
  const customMeters = Array.isArray(agency.customMeters) ? agency.customMeters.flatMap((candidate, index) => {
    const meter = record(candidate);
    if (!Object.keys(meter).length) return [];
    const rawId = String(meter.id ?? `meter_${index + 1}`).toLowerCase().replace(/[^a-z0-9_]/g, "_");
    const id = /^[a-z]/.test(rawId) ? rawId.slice(0, 32) : `meter_${rawId}`.slice(0, 32);
    return [{ id, label: String(meter.label ?? `Meter ${index + 1}`), min: Number(meter.min ?? 0), max: Number(meter.max ?? 100), initial: Number(meter.initial ?? 0), visible: meter.visible !== false }];
  }) : [];
  const characters = Array.isArray(source.characters) ? source.characters.flatMap((candidate) => {
    const character = record(candidate);
    if (typeof character.characterId !== "string") return [];
    return [{ characterId: character.characterId, characterVersionId: typeof character.characterVersionId === "string" ? character.characterVersionId : null, name: String(character.name ?? "Character") }];
  }) : [];
  return {
    basics: {
      title: String(basics.title ?? source.title ?? fallback.basics.title),
      tone: String(basics.tone ?? fallback.basics.tone),
      genre: String(basics.genre ?? fallback.basics.genre),
      storyOutline: String(basics.storyOutline ?? source.premise ?? fallback.basics.storyOutline),
      openingScene: String(basics.openingScene ?? source.openingScene ?? fallback.basics.openingScene),
      pacing,
      targetTurns: Math.max(1, Number.isInteger(basics.targetTurns) ? Number(basics.targetTurns) : fallback.basics.targetTurns),
    },
    themes: {
      transformation: stringArray(themes.transformation).map((item) => transformationAliases[item] ?? item).filter((item) => allowedTransformationThemes.has(item)),
      adult: stringArray(themes.adult).map((item) => adultAliases[item] ?? item).filter((item) => allowedAdultThemes.has(item)),
    },
    agency: {
      mode: agency.mode === "narrative" || agency.mode === "strict_roleplay" ? agency.mode : "mechanical",
      femStart: Math.max(0, Math.min(100, Number(agency.femStart ?? fallback.agency.femStart))),
      corStart: Math.max(0, Math.min(100, Number(agency.corStart ?? fallback.agency.corStart))),
      customMeters,
    },
    transformations: namedItems(source.transformations),
    sexScenes: namedItems(source.sexScenes),
    characters,
    world: { description: String(world.description ?? ""), maps },
    chapters,
    startingChapterId: typeof source.startingChapterId === "string" ? source.startingChapterId : chapters[0]?.id ?? null,
    mechanics: {
      mapMovement: mechanics.mapMovement !== false,
      tabletopBasics: mechanics.tabletopBasics !== false,
      diceRolls: mechanics.diceRolls !== false,
    },
    publicInfo: {
      description: String(publicInfo.description ?? source.summary ?? ""),
      tags: stringArray(publicInfo.tags ?? source.tags),
      bannerMediaId: typeof publicInfo.bannerMediaId === "string" ? publicInfo.bannerMediaId : null,
    },
  };
}

export type SharedCampaignDetail = {
  id: string;
  ownerId?: string;
  visibility: CreationVisibility;
  title: string;
  summary: string;
  versionId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  tone: string;
  genre: string;
  pacing: string;
  targetTurns: number | null;
  tags: string[];
  transformationThemes: string[];
  adultThemes: string[];
  warnings: string[];
  mechanics: string[];
  agencyMode: string;
  femStart: number | null;
  corStart: number | null;
  bannerMediaId: string | null;
  maps: Array<{
    id: string;
    name: string;
    description: string;
    width: number | null;
    height: number | null;
    moodMediaId: string | null;
  }>;
};

function normalizeSharedCampaign(value: unknown): SharedCampaignDetail {
  const payload = record(value);
  const game = record(payload.game ?? value);
  const versions = Array.isArray(payload.versions) ? payload.versions.map(record) : [];
  const selectedVersionId = typeof game.publishedVersionId === "string"
    ? game.publishedVersionId
    : typeof game.currentVersionId === "string"
      ? game.currentVersionId
      : null;
  const version = versions.find((candidate) => candidate.id === selectedVersionId) ?? versions[0] ?? {};
  const directDefinition = record(game.definition);
  const versionCreatorDefinition = record(version.creatorDefinition);
  const creatorDefinition = Object.keys(record(directDefinition.basics)).length > 0 ? directDefinition : versionCreatorDefinition;
  const runtimeDefinition = Object.keys(record(game.runtimeDefinition)).length > 0 ? record(game.runtimeDefinition) : record(version.definition ?? directDefinition);
  const basics = record(creatorDefinition.basics);
  const themes = record(creatorDefinition.themes);
  const agency = record(creatorDefinition.agency);
  const publicInfo = record(creatorDefinition.publicInfo);
  const runtimePolicy = record(runtimeDefinition.transformationPolicy);
  const creatorWorld = record(creatorDefinition.world);
  const runtimeWorld = record(runtimeDefinition.starterWorld);
  const rawMaps = Array.isArray(creatorWorld.maps) ? creatorWorld.maps : Array.isArray(runtimeWorld.maps) ? runtimeWorld.maps : [];
  const now = new Date().toISOString();
  return {
    id: String(game.id ?? crypto.randomUUID()),
    ...(typeof game.ownerId === "string" ? { ownerId: game.ownerId } : {}),
    visibility: normalizeVisibility(game.visibility),
    title: String(basics.title ?? game.title ?? "Untitled campaign"),
    summary: String(publicInfo.description ?? game.summary ?? ""),
    versionId: String(version.id ?? game.currentVersionId ?? game.publishedVersionId ?? game.id ?? ""),
    version: Number.isInteger(version.version) ? Number(version.version) : Number.isInteger(game.revision) ? Number(game.revision) : 1,
    createdAt: String(game.createdAt ?? version.createdAt ?? now),
    updatedAt: String(game.updatedAt ?? version.createdAt ?? now),
    tone: String(basics.tone ?? runtimeDefinition.tone ?? ""),
    genre: String(basics.genre ?? ""),
    pacing: String(basics.pacing ?? runtimePolicy.pacing ?? ""),
    targetTurns: Number.isFinite(Number(basics.targetTurns)) ? Number(basics.targetTurns) : null,
    tags: stringArray(publicInfo.tags).length > 0 ? stringArray(publicInfo.tags) : stringArray(runtimeDefinition.tags),
    transformationThemes: stringArray(record(themes).transformation),
    adultThemes: stringArray(record(themes).adult),
    warnings: stringArray(runtimeDefinition.warnings),
    mechanics: stringArray(runtimeDefinition.modules),
    agencyMode: String(agency.mode ?? runtimePolicy.agencyMode ?? ""),
    femStart: nullableNumber(agency.femStart ?? runtimePolicy.femStart),
    corStart: nullableNumber(agency.corStart ?? runtimePolicy.corStart),
    bannerMediaId: typeof publicInfo.bannerMediaId === "string" ? publicInfo.bannerMediaId : null,
    maps: rawMaps.flatMap((candidate, index) => {
      const map = record(candidate);
      if (Object.keys(map).length === 0) return [];
      return [{
        id: String(map.id ?? `map-${index + 1}`),
        name: String(map.name ?? `Map ${index + 1}`),
        description: String(map.description ?? ""),
        width: nullableNumber(map.width),
        height: nullableNumber(map.height),
        moodMediaId: typeof map.moodMediaId === "string" ? map.moodMediaId : null,
      }];
    }),
  };
}

export function normalizeVeniceConnection(value: unknown): VeniceConnection {
  const envelope = record(value);
  const raw = record(envelope.connection ?? value);
  const rawBalance = record(raw.balance);
  const rawCapabilities = record(raw.capabilities);
  const allowedStatuses: VeniceConnectionStatus[] = ["ready", "funding_required", "reconnect_required", "unverified"];
  const status = allowedStatuses.includes(raw.status as VeniceConnectionStatus) ? raw.status as VeniceConnectionStatus : "unverified";
  return {
    connected: raw.connected === true,
    status,
    keySuffix: typeof raw.keySuffix === "string" ? raw.keySuffix : null,
    connectedAt: typeof raw.connectedAt === "string" ? raw.connectedAt : null,
    expiresAt: typeof raw.expiresAt === "string" ? raw.expiresAt : null,
    lastCheckedAt: typeof raw.lastCheckedAt === "string" ? raw.lastCheckedAt : null,
    accessPermitted: raw.accessPermitted === true,
    balance: {
      spendableCredits: nullableNumber(rawBalance.spendableCredits),
      bundledCredits: nullableNumber(rawBalance.bundledCredits),
      usdCredits: nullableNumber(rawBalance.usdCredits),
      diemCredits: nullableNumber(rawBalance.diemCredits),
    },
    capabilities: {
      text: rawCapabilities.text === true,
      imageGeneration: rawCapabilities.imageGeneration === true,
      imageEditing: rawCapabilities.imageEditing === true,
    },
  };
}

function normalizeSession(value: BackendSession, usage?: BackendUsage): Session {
  activeCsrfToken = value.csrfToken;
  const quota = usage ?? value.usage ?? { dailyUsed: 0, monthlyUsed: 0, monthlyLimit: 2_000_000 };
  return {
    user: { id: value.user.id, username: value.user.username, role: value.user.role === "admin" ? "admin" : "player", verifiedAdult: value.user.adultVerificationStatus === "verified" },
    quota: { dailyUsed: quota.dailyUsed, monthlyUsed: quota.monthlyUsed, monthlyLimit: quota.monthlyLimit },
    venice: value.venice === undefined
      ? { connected: false, status: "unverified" }
      : ((connection) => ({ connected: connection.connected, status: connection.status }))(normalizeVeniceConnection(value.venice)),
    csrfToken: value.csrfToken,
  };
}

type RawGame = { id: string; title: string; summary: string; ownerId: string; visibility: string; currentVersionId: string; updatedAt?: unknown; versionId?: unknown; bannerMediaId?: unknown; definition?: { tags?: unknown; warnings?: unknown; modules?: unknown; starterWorld?: unknown } };
const stringArray = (value: unknown): string[] => Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
const record = (value: unknown): Record<string, unknown> => typeof value === "object" && value !== null ? value as Record<string, unknown> : {};

function catalogAdapter(game: RawGame, index: number): CatalogGame {
  const definition = game.definition ?? {};
  const starter = record(definition.starterWorld);
  const maps = Array.isArray(starter.maps) ? starter.maps : [];
  const accents: CatalogGame["accent"][] = ["violet", "teal", "rose", "amber"];
  const tags = stringArray(definition.tags);
  const modules = stringArray(definition.modules);
  return {
    id: game.id, ownerId: game.ownerId, versionId: typeof game.versionId === "string" ? game.versionId : game.currentVersionId, bannerMediaId: typeof game.bannerMediaId === "string" ? game.bannerMediaId : null, updatedAt: typeof game.updatedAt === "string" ? game.updatedAt : "", title: game.title,
    tagline: game.title === "Ezekiel Storm" ? "A bargain written in candlelight." : "A persistent world shaped by every choice.",
    description: game.summary,
    creator: game.title === "Ezekiel Storm" ? "tfGames.ai Studio" : "Community creator",
    tags: tags.length ? tags : ["Story", "Transformation"], warnings: stringArray(definition.warnings), mechanics: modules.slice(0, 3),
    accent: accents[index % accents.length]!, rooms: maps.length || 1, saves: 0,
    ...(game.title === "Ezekiel Storm" ? { featured: true } : {}),
  };
}

type RawCampaignListItem = { id: string; gameVersionId: string; title?: string; worldVersion: number; updatedAt: string; playerName: string };
function campaignSummaryAdapter(item: RawCampaignListItem): CampaignSummary {
  return { id: item.id, gameId: item.gameVersionId, title: item.title?.trim() || `${item.playerName}'s campaign`, chapter: item.worldVersion ? `World version ${item.worldVersion}` : "The opening scene", location: "Starting area", turn: item.worldVersion, updatedAt: new Date(item.updatedAt).toLocaleDateString(), progress: Math.min(95, item.worldVersion * 4), stats: { FEM: 0, COR: 0 } };
}

export function mapAdapter(world: Record<string, unknown>, playerId: string, presentation: Record<string, unknown> = {}): MapModel {
  const maps = record(world.maps);
  const actorProfileMediaIds = record(presentation.actorProfileMediaIds);
  const presentedMaps = record(presentation.maps);
  const player = record(record(world.actors)[playerId]);
  const playerMapId = typeof record(player.position).mapId === "string" ? String(record(player.position).mapId) : null;
  const mapEntries = Object.entries(maps);
  const selectedEntry = (playerMapId === null ? undefined : mapEntries.find(([key, value]) => key === playerMapId || record(value).id === playerMapId)) ?? mapEntries[0];
  const selectedMapKey = selectedEntry?.[0] ?? "starting-area";
  const firstMap = record(selectedEntry?.[1]);
  const mapId = String(firstMap.id ?? selectedMapKey);
  const presentedMap = record(presentedMaps[mapId] ?? presentedMaps[selectedMapKey]);
  const selectedMapIds = new Set([selectedMapKey, mapId]);
  const cells = Object.values(record(firstMap.cells)).map(record); const coordinates = cells.map((cell) => record(cell.coordinate));
  const inferredWidth = Math.max(8, ...coordinates.map((coordinate) => Number(coordinate.x ?? 0) + 1));
  const inferredHeight = Math.max(6, ...coordinates.map((coordinate) => Number(coordinate.y ?? 0) + 1));
  const width = typeof firstMap.width === "number" && Number.isInteger(firstMap.width) ? Math.max(4, firstMap.width) : inferredWidth;
  const height = typeof firstMap.height === "number" && Number.isInteger(firstMap.height) ? Math.max(4, firstMap.height) : inferredHeight;
  const tiles: MapModel["tiles"] = cells.flatMap((cell) => {
    const coordinate = record(cell.coordinate);
    if (typeof coordinate.x !== "number" || typeof coordinate.y !== "number") return [];
    return [{
      x: coordinate.x,
      y: coordinate.y,
      terrain: String(cell.terrain ?? "floor"),
      walkable: cell.walkable !== false,
      roomId: typeof cell.roomId === "string" ? cell.roomId : null,
    }];
  });
  const roomCells = new Map<string, Array<{ x: number; y: number }>>();
  for (const tile of tiles) {
    if (!tile.walkable || tile.roomId === null) continue;
    const bucket = roomCells.get(tile.roomId) ?? [];
    bucket.push({ x: tile.x, y: tile.y });
    roomCells.set(tile.roomId, bucket);
  }
  const roomColors = [0x252238, 0x1b2930, 0x30251f, 0x242a25, 0x2d2030];
  const rooms: MapModel["rooms"] = [...roomCells.entries()].map(([id, roomTiles], index) => {
    const xs = roomTiles.map((tile) => tile.x); const ys = roomTiles.map((tile) => tile.y);
    const minX = Math.min(...xs); const minY = Math.min(...ys);
    const label = id.replaceAll("_", " ").replace(/(^|\s)\S/g, (character) => character.toUpperCase());
    return { id, label, x: minX, y: minY, width: Math.max(...xs) - minX + 1, height: Math.max(...ys) - minY + 1, color: roomColors[index % roomColors.length]! };
  });
  if (rooms.length === 0) rooms.push({ id: mapId, label: String(firstMap.name ?? "Starting Area"), x: 0, y: 0, width, height, color: 0x252238 });

  const walls: MapModel["walls"] = [];
  const doors: MapModel["doors"] = [];
  const seenEdges = new Set<string>();
  const addEdge = (x1: number, y1: number, x2: number, y2: number, kind: string): void => {
    const key = `${Math.min(x1, x2)},${Math.min(y1, y2)}:${Math.max(x1, x2)},${Math.max(y1, y2)}`;
    if (seenEdges.has(key)) return;
    seenEdges.add(key);
    if (kind === "door") doors.push({ x: (x1 + x2) / 2, y: (y1 + y2) / 2, orientation: y1 === y2 ? "h" : "v" });
    else walls.push({ x1, y1, x2, y2 });
  };
  const tileByCoordinate = new Map(tiles.map((tile) => [`${tile.x},${tile.y}`, tile]));
  for (const cell of cells) {
    const coordinate = record(cell.coordinate);
    if (typeof coordinate.x !== "number" || typeof coordinate.y !== "number") continue;
    const x = coordinate.x; const y = coordinate.y;
    const edges = record(cell.edges);
    for (const [direction, rawEdge] of Object.entries(edges)) {
      const edge = record(rawEdge);
      if (edge.kind !== "wall" && edge.kind !== "door") continue;
      if (direction === "north") addEdge(x, y, x + 1, y, String(edge.kind));
      if (direction === "east") addEdge(x + 1, y, x + 1, y + 1, String(edge.kind));
      if (direction === "south") addEdge(x, y + 1, x + 1, y + 1, String(edge.kind));
      if (direction === "west") addEdge(x, y, x, y + 1, String(edge.kind));
    }
    const tile = tileByCoordinate.get(`${x},${y}`);
    if (tile?.walkable !== true) continue;
    const neighbors = [
      { key: `${x},${y - 1}`, edge: [x, y, x + 1, y] as const },
      { key: `${x + 1},${y}`, edge: [x + 1, y, x + 1, y + 1] as const },
      { key: `${x},${y + 1}`, edge: [x, y + 1, x + 1, y + 1] as const },
      { key: `${x - 1},${y}`, edge: [x, y, x, y + 1] as const },
    ];
    for (const neighbor of neighbors) {
      const next = tileByCoordinate.get(neighbor.key);
      if (next !== undefined && !next.walkable) addEdge(...neighbor.edge, "wall");
    }
  }
  const tokens: MapModel["tokens"] = [];
  for (const actor of Object.values(record(world.actors)).map(record)) {
    const position = record(actor.position); const coordinate = record(position.coordinate); const identity = record(actor.identity);
    if (!selectedMapIds.has(String(position.mapId ?? mapId)) || typeof coordinate.x !== "number" || typeof coordinate.y !== "number") continue;
    const id = String(actor.id ?? crypto.randomUUID()); const isPlayer = id === playerId;
    const name = String(identity.name ?? "Unknown character");
    const overallAppearance = record(record(actor.body).overallAppearance);
    tokens.push({
      id,
      label: name.slice(0, 1).toUpperCase(),
      name,
      pronouns: String(identity.pronouns ?? ""),
      description: String(identity.selfDescription ?? overallAppearance.description ?? ""),
      profileMediaId: typeof actorProfileMediaIds[id] === "string" ? actorProfileMediaIds[id] : null,
      x: coordinate.x,
      y: coordinate.y,
      kind: isPlayer ? "player" : "npc",
      color: isPlayer ? 0xb9a4ff : 0xff668d,
    });
  }
  for (const object of Object.values(record(world.objects)).map(record)) {
    const position = record(object.position); const coordinate = record(position.coordinate);
    if (!selectedMapIds.has(String(position.mapId ?? mapId)) || typeof coordinate.x !== "number" || typeof coordinate.y !== "number") continue;
    tokens.push({ id: String(object.id ?? crypto.randomUUID()), label: "◆", name: String(object.name ?? "Object"), pronouns: "", description: String(object.description ?? ""), profileMediaId: null, x: coordinate.x, y: coordinate.y, kind: "object", color: 0x57d8c7 });
  }
  return {
    id: mapId,
    name: String(firstMap.name ?? "Starting Area"),
    description: String(presentedMap.description ?? ""),
    moodMediaId: typeof presentedMap.moodMediaId === "string" ? presentedMap.moodMediaId : null,
    floor: Number(firstMap.floor ?? 0),
    width,
    height,
    cellFeet: 5,
    rooms,
    tiles,
    walls,
    doors,
    tokens,
    playerId,
  };
}

function changeAdapter(value: unknown, index: number): TurnChange {
  const item = record(value); const domain = String(item.domain ?? "world");
  const categories: Record<string, TurnChange["category"]> = { stat: "stat", transformation: "body", knowledge: "mind", actor: "identity", relationship: "relationship", quest: "quest" };
  return { id: `change-${index}`, category: categories[domain] ?? "world", label: String(item.field ?? domain), before: JSON.stringify(item.before ?? ""), after: JSON.stringify(item.after ?? "Changed"), reason: String(item.reason ?? item.source ?? "World update") };
}

const humanizeId = (value: string): string => value
  .replaceAll("_", " ")
  .replace(/\s+/g, " ")
  .trim()
  .replace(/(^|\s)\S/g, (character) => character.toUpperCase());

export function campaignAdapter(payload: Record<string, unknown>): Campaign {
  const rawCampaign = record(payload.campaign); const state = record(rawCampaign.world); const world = record(state.world);
  const game = record(payload.game);
  const persona = record(rawCampaign.personaSnapshot); const actors = record(world.actors); const playerId = String(state.playerActorId ?? "player"); const actor = record(actors[playerId]);
  const identity = record(actor.identity); const mechanics = record(actor.mechanics); const stats = record(actor.stats);
  const core: CoreStats = { STR: Number(stats.STR ?? 10), DEX: Number(stats.DEX ?? 10), CON: Number(stats.CON ?? 10), INT: Number(stats.INT ?? 10), WIS: Number(stats.WIS ?? 10), CHA: Number(stats.CHA ?? 10), FEM: Number(stats.FEM ?? 0), COR: Number(stats.COR ?? 0) };
  const turns = Array.isArray(payload.turns) ? payload.turns.map(record) : [];
  const currentBranchId = typeof rawCampaign.currentBranchId === "string" ? rawCampaign.currentBranchId : null;
  const latestResolvedTurn = [...turns].reverse().find((turn) => ["completed", "check_required"].includes(String(turn.status ?? "")));
  const latestRegeneratableTurnId = latestResolvedTurn?.status === "completed"
    && (currentBranchId === null || latestResolvedTurn.branchId === currentBranchId)
    && typeof latestResolvedTurn.id === "string"
    ? latestResolvedTurn.id
    : null;
  const openingScene = typeof game.openingScene === "string" ? game.openingScene.trim() : "";
  const story: StoryMessage[] = openingScene
    ? [{ id: "opening", speaker: "Narrator", text: openingScene, kind: "narrator" }]
    : [];
  story.push(...turns.flatMap((turn, index) => {
    const status = String(turn.status ?? "completed");
    if (!(["completed", "check_required"] as const).includes(status as "completed" | "check_required")) return [];
    const turnId = typeof turn.id === "string" && turn.id.length > 0 ? turn.id : `turn-${index}`;
    return [
      { id: `action-${turnId}`, turnId, speaker: "You", text: String(turn.action ?? ""), kind: "player" as const },
      ...(String(turn.narration ?? "")
        ? [{ id: `narration-${turnId}`, turnId, regeneratable: turn.id === latestRegeneratableTurnId, speaker: "Narrator", text: String(turn.narration), kind: "narrator" as const }]
        : []),
    ];
  }));
  if (!story.length) story.push({ id: "opening", speaker: "Narrator", text: "The world waits for your first action.", kind: "narrator" });
  const lastResult = record(turns.at(-1)?.result); const receipt = record(lastResult.receipt); const receiptChanges = Array.isArray(receipt.changes) ? receipt.changes : [];
  const transformations = Array.isArray(state.transformations) ? state.transformations.map(record) : [];
  const timeline: TimelineEvent[] = transformations.map((event, index) => ({ id: String(event.id ?? `event-${index}`), turn: Number(event.turn ?? 0), title: String(event.stage ?? event.domain ?? "Transformation"), summary: String(event.cause ?? "A persistent change was recorded."), category: (["body", "mind", "identity", "social", "perception"].includes(String(event.domain)) ? String(event.domain) : "body") as TimelineEvent["category"], reversible: Boolean(event.reversible) }));
  const objects = record(world.objects); const inventoryIds = Array.isArray(actor.inventoryObjectIds) ? actor.inventoryObjectIds.map(String) : [];
  const quests = Object.values(record(world.quests)).map(record).filter((quest) => quest.visible !== false);
  const conditions = Array.isArray(mechanics.conditions) ? mechanics.conditions.map((condition) => String(record(condition).label ?? "Condition")) : [];
  const media = record(payload.media);
  const minutes = Number(world.worldTimeMinutes ?? 0); const projectedMap = mapAdapter(world, playerId, media); const mapName = projectedMap.name;
  const playerToken = projectedMap.tokens.find((token) => token.id === playerId);
  const playerCharacterSnapshot = record(rawCampaign.playerCharacterSnapshot);
  const pendingTurn = [...turns].reverse().find((turn) => turn.status === "check_required");
  const pending = record(pendingTurn?.pendingCheck ?? state.pendingCheck);
  const pendingSource = record(pending.source);
  const source = pendingSource.kind === "custom_meter"
    ? humanizeId(String(pendingSource.meterId ?? "meter"))
    : pendingSource.kind === "skill"
      ? humanizeId(String(pendingSource.skillId ?? "skill"))
      : String(pendingSource.stat ?? "Check");
  return {
    id: String(rawCampaign.id), gameId: String(rawCampaign.gameVersionId), title: typeof rawCampaign.title === "string" && rawCampaign.title.trim() ? rawCampaign.title : `${String(identity.name ?? persona.name ?? "Explorer")}'s campaign`, chapter: typeof game.currentSceneName === "string" && game.currentSceneName.trim() ? game.currentSceneName : humanizeId(String(world.sceneId ?? "Opening scene")), location: mapName, turn: Number(state.turnNumber ?? 0), updatedAt: String(rawCampaign.updatedAt ?? "Now"), progress: Math.min(95, Number(state.turnNumber ?? 0) * 4), stats: { FEM: core.FEM, COR: core.COR }, worldVersion: Number(rawCampaign.worldVersion ?? state.worldVersion ?? 0), time: `Day ${Math.floor(minutes / 1440) + 1} · ${String(Math.floor((minutes % 1440) / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`,
    player: { name: String(identity.name ?? persona.name ?? "Explorer"), pronouns: String(identity.pronouns ?? persona.pronouns ?? "they / them"), appearance: String(identity.selfDescription ?? playerCharacterSnapshot.appearance ?? persona.appearance ?? ""), profileMediaId: playerToken?.profileMediaId ?? (typeof playerCharacterSnapshot.profileMediaId === "string" ? playerCharacterSnapshot.profileMediaId : null), level: 1, hp: Number(mechanics.hp ?? 10), maxHp: Number(mechanics.maxHp ?? 10), defense: Number(mechanics.defense ?? 10), speed: Number(mechanics.speed ?? 30), stats: core, conditions },
    map: projectedMap, story,
    pendingCheck: pendingTurn === undefined || typeof pending.id !== "string" ? null : {
      turnId: String(pendingTurn.id),
      reason: String(pending.reason ?? "Resolve the check"),
      stakes: String(pending.stakes ?? "The outcome changes what happens next."),
      source,
      modifier: Number(pending.modifier ?? 0),
      dc: Number(pending.dc ?? 10),
      advantage: (["advantage", "disadvantage"] as const).includes(pending.advantage as "advantage" | "disadvantage")
        ? pending.advantage as "advantage" | "disadvantage"
        : "normal",
    },
    aiTurns: turns.map((turn) => ({
      id: String(turn.id ?? ""),
      status: (["queued", "generating", "check_required", "completed", "error", "cancelled"] as const).includes(turn.status as "queued")
        ? turn.status as "queued" | "generating" | "check_required" | "completed" | "error" | "cancelled"
        : "error",
      action: String(turn.action ?? ""),
      createdAt: String(turn.createdAt ?? ""),
    })).filter((turn) => turn.id.length > 0).reverse(),
    changes: receiptChanges.map(changeAdapter), timeline,
    inventory: inventoryIds.map((id) => { const object = record(objects[id]); return { name: String(object.name ?? "Unknown object"), detail: String(object.description ?? "Carried item"), equipped: Array.isArray(actor.equippedObjectIds) && actor.equippedObjectIds.includes(id) }; }),
    quests: quests.map((quest) => ({ title: String(quest.title ?? "Quest"), detail: String(quest.stage ?? "Active"), progress: quest.status === "completed" ? 100 : 25 })),
  };
}

type RawAsset = { id: string; kind: "character" | "map" | "object" | "ability" | "rule_preset"; title: string; definition: Record<string, unknown>; visibility: "private" | "published"; version: number; updatedAt: string };
function libraryAdapter(asset: RawAsset): LibraryAsset {
  const kinds: Record<RawAsset["kind"], LibraryAsset["type"]> = { character: "character", map: "location", object: "object", ability: "ability", rule_preset: "rules" };
  return { id: asset.id, type: kinds[asset.kind], name: asset.title, description: typeof asset.definition.description === "string" ? asset.definition.description : "Reusable creator asset", visibility: asset.visibility, version: asset.version, updatedAt: new Date(asset.updatedAt).toLocaleDateString() };
}

export const api = {
  dialectState(): Promise<DialectState> {
    return request<DialectState>("/dialect-coach/state");
  },
  async createDialectSession(input: { mode: "academy" | "free_practice"; lessonId: string | null; mentorTone: DialectMentorTone }): Promise<DialectSession> {
    return (await request<{ session: DialectSession }>("/dialect-coach/sessions", { method: "POST", body: JSON.stringify(input) })).session;
  },
  async dialectSession(id: string): Promise<{ session: DialectSession; attempts: DialectAttempt[] }> {
    return request(`/dialect-coach/sessions/${encodeURIComponent(id)}`);
  },
  async updateDialectSession(id: string, mentorTone: DialectMentorTone): Promise<DialectSession> {
    return (await request<{ session: DialectSession }>(`/dialect-coach/sessions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ mentorTone }) })).session;
  },
  submitDialectAttempt(id: string, input: { practiceMode: DialectPracticeMode; referenceText: string | null; lineId: string | null; audioBase64: string }): Promise<{ attempt: DialectAttempt; completion: unknown; state: DialectState }> {
    return request(`/dialect-coach/sessions/${encodeURIComponent(id)}/attempts`, { method: "POST", body: JSON.stringify({ practiceMode: input.practiceMode, referenceText: input.referenceText, lineId: input.lineId, audio: { format: "wav", data: input.audioBase64 } }) });
  },
  escapeDialectLesson(lessonId: string): Promise<{ state: DialectState }> {
    return request(`/dialect-coach/academy/lessons/${encodeURIComponent(lessonId)}/escape`, { method: "PUT" });
  },
  deleteDialectSession(id: string): Promise<void> {
    return request(`/dialect-coach/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
  resetDialectData(): Promise<void> {
    return request("/dialect-coach/data", { method: "DELETE" });
  },
  async session(): Promise<Session | null> {
    if (DEMO_MODE) return demoSignedIn() ? demoSession : null;
    try { return normalizeSession(await request<BackendSession>("/auth/me")); }
    catch (error) { if (error instanceof ApiError && error.status === 401) return null; throw error; }
  },
  async login(username: string, password: string): Promise<Session> {
    return run(async () => { const auth = await request<BackendSession>("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }); activeCsrfToken = auth.csrfToken; const usage = await request<BackendUsage>("/usage"); return normalizeSession(auth, usage); }, () => { if (!username.trim() || !password) throw new Error("Enter a username and password."); setDemoSignedIn(true); return { ...demoSession, user: { ...demoSession.user, username: username.trim() } }; });
  },
  async acceptInvite(payload: { token: string; username: string; password: string; consents: string[] }): Promise<Session> {
    return run(async () => { const auth = await request<BackendSession>(`/auth/invites/${encodeURIComponent(payload.token)}/accept`, { method: "POST", body: JSON.stringify({ username: payload.username, password: payload.password, consents: { terms: true, privacy: true, aiContent: true, sensitiveData: true, adultAffirmation: true } }) }); activeCsrfToken = auth.csrfToken; const usage = await request<BackendUsage>("/usage"); return normalizeSession(auth, usage); }, () => { setDemoSignedIn(true); return { ...demoSession, user: { ...demoSession.user, username: payload.username.trim() } }; });
  },
  async logout(): Promise<void> { await run(() => request<void>("/auth/logout", { method: "POST" }), () => undefined); activeCsrfToken = null; setDemoSignedIn(false); },
  veniceConnection(): Promise<VeniceConnection> {
    return run(async () => normalizeVeniceConnection(await request<unknown>("/integrations/venice")), () => demoVenice);
  },
  connectVenice(apiKey: string): Promise<VeniceConnection> {
    return run(
      async () => normalizeVeniceConnection(await request<unknown>("/integrations/venice", { method: "PUT", body: JSON.stringify({ apiKey }) })),
      () => {
        if (!apiKey.trim()) throw new ApiError(400, "VENICE_KEY_REQUIRED", "Enter a Venice API key.");
        demoVenice = { ...demoVenice, connected: true, status: "ready", accessPermitted: true, keySuffix: apiKey.trim().slice(-4), lastCheckedAt: new Date().toISOString() };
        return demoVenice;
      },
    );
  },
  refreshVenice(): Promise<VeniceConnection> {
    return run(
      async () => normalizeVeniceConnection(await request<unknown>("/integrations/venice/refresh", { method: "POST" })),
      () => { demoVenice = { ...demoVenice, lastCheckedAt: new Date().toISOString() }; return demoVenice; },
    );
  },
  disconnectVenice(): Promise<void> {
    return run(
      () => request<void>("/integrations/venice", { method: "DELETE" }),
      () => { demoVenice = { ...EMPTY_VENICE_CONNECTION }; demoMedia = []; demoImageJobs.clear(); },
    );
  },
  imageQuote(kind: "generate" | "edit", resolution: ImageResolution): Promise<ImageQuote> {
    return run(
      () => request<ImageQuote>("/images/quote", { method: "POST", body: JSON.stringify({ kind, resolution }) }),
      () => ({ model: kind === "edit" ? "qwen-image-edit" : "qwen-image", estimatedUsd: resolution === "2K" ? 0.09 : 0.05, estimatedCredits: resolution === "2K" ? 9 : 5 }),
    );
  },
  generateImage(input: { prompt: string; aspectRatio: ImageAspectRatio; resolution: ImageResolution; outputFormat?: ImageOutputFormat; idempotencyKey?: string }): Promise<ImageJob> {
    const { idempotencyKey, ...payload } = input;
    return run(
      async () => (await request<{ job: ImageJob }>("/images/generations", {
        method: "POST",
        body: JSON.stringify(payload),
        ...(idempotencyKey === undefined ? {} : { headers: { "Idempotency-Key": idempotencyKey } }),
      })).job,
      () => demoImageJob("generate", payload),
    );
  },
  editImage(input: { prompt: string; sourceMediaId: string; aspectRatio?: ImageAspectRatio; resolution: ImageResolution; outputFormat?: ImageOutputFormat; idempotencyKey?: string }): Promise<ImageJob> {
    const { idempotencyKey, ...payload } = input;
    return run(
      async () => (await request<{ job: ImageJob }>("/images/edits", {
        method: "POST",
        body: JSON.stringify(payload),
        ...(idempotencyKey === undefined ? {} : { headers: { "Idempotency-Key": idempotencyKey } }),
      })).job,
      () => demoImageJob("edit", { ...payload, aspectRatio: payload.aspectRatio ?? "auto" }),
    );
  },
  imageJob(id: string): Promise<ImageJob> {
    return run(
      async () => (await request<{ job: ImageJob }>(`/image-jobs/${encodeURIComponent(id)}`)).job,
      () => demoImageJobs.get(id) ?? (() => { throw new ApiError(404, "NOT_FOUND", "Image job not found."); })(),
    );
  },
  media(): Promise<MediaAsset[]> {
    return run(async () => (await request<{ media: MediaAsset[] }>("/media")).media, () => demoMedia);
  },
  deleteMedia(id: string): Promise<void> {
    return run(() => request<void>(`/media/${encodeURIComponent(id)}`, { method: "DELETE" }), () => { demoMedia = demoMedia.filter((asset) => asset.id !== id); });
  },
  characterDrafts(options: { scope?: "all" | "mine" | "public"; status?: "all" | "draft" | "ready"; search?: string } = {}): Promise<CharacterDraft[]> {
    const query = new URLSearchParams();
    if (options.scope) query.set("scope", options.scope);
    if (options.status) query.set("status", options.status);
    if (options.search?.trim()) query.set("search", options.search.trim());
    const suffix = query.size ? `?${query.toString()}` : "";
    return run(
      async () => {
        const payload = await request<unknown>(`/characters/drafts${suffix}`);
        const envelope = record(payload);
        const items = Array.isArray(envelope.characters) ? envelope.characters : Array.isArray(envelope.drafts) ? envelope.drafts : Array.isArray(payload) ? payload : [];
        return items.map(normalizeCharacterDraft);
      },
      () => [...demoCharacterDrafts.values()].filter((item) => (!options.status || options.status === "all" || item.status === options.status) && (!options.search || item.definition.name.toLowerCase().includes(options.search.toLowerCase()))),
    );
  },
  characterDraft(id: string): Promise<CharacterDraft> {
    return run(
      async () => normalizeCharacterDraft(await request<unknown>(`/characters/drafts/${encodeURIComponent(id)}`)),
      () => demoCharacterDrafts.get(id) ?? (() => { throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character draft not found."); })(),
    );
  },
  sharedCharacter(id: string, shareToken?: string): Promise<CharacterDraft> {
    const suffix = shareToken ? `?${new URLSearchParams({ share: shareToken }).toString()}` : "";
    return run(
      async () => normalizeCharacterDraft(await request<unknown>(`/characters/${encodeURIComponent(id)}${suffix}`)),
      () => demoCharacterDrafts.get(id) ?? (() => { throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character not found."); })(),
    );
  },
  createCharacterDraft(input: { definition: CharacterDefinition; visibility?: CreationVisibility }): Promise<CharacterDraft> {
    return run(
      async () => normalizeCharacterDraft(await request<unknown>("/characters/drafts", { method: "POST", body: JSON.stringify(input) })),
      () => {
        const now = new Date().toISOString();
        const draft: CharacterDraft = { id: crypto.randomUUID(), status: "draft", revision: 0, visibility: input.visibility ?? "private", definition: structuredClone(input.definition), createdAt: now, updatedAt: now };
        demoCharacterDrafts.set(draft.id, draft);
        return draft;
      },
    );
  },
  updateCharacterDraft(id: string, input: { expectedRevision: number; title?: string; visibility?: CreationVisibility; definition?: CharacterDefinition }): Promise<CharacterDraft> {
    return run(
      async () => normalizeCharacterDraft(await request<unknown>(`/characters/drafts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) })),
      () => {
        const current = demoCharacterDrafts.get(id);
        if (!current) throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character draft not found.");
        if (current.revision !== input.expectedRevision) throw new ApiError(409, "STALE_DRAFT_REVISION", "This draft changed elsewhere.");
        const next = { ...current, ...(input.visibility ? { visibility: input.visibility } : {}), ...(input.definition ? { definition: structuredClone(input.definition) } : {}), revision: current.revision + 1, updatedAt: new Date().toISOString() };
        demoCharacterDrafts.set(id, next);
        return next;
      },
    );
  },
  finalizeCharacterDraft(id: string, expectedRevision: number): Promise<CharacterDraft> {
    return run(
      async () => normalizeCharacterDraft(await request<unknown>(`/characters/drafts/${encodeURIComponent(id)}/finalize`, { method: "POST", body: JSON.stringify({ expectedRevision }) })),
      () => {
        const current = demoCharacterDrafts.get(id);
        if (!current) throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character draft not found.");
        const next = { ...current, status: "ready" as const, revision: current.revision + 1, updatedAt: new Date().toISOString() };
        demoCharacterDrafts.set(id, next);
        return next;
      },
    );
  },
  rotateCharacterShare(id: string): Promise<{ character: CharacterDraft; shareToken: string; shareUrl: string }> {
    return run(async () => {
      const payload = record(await request<unknown>(`/characters/drafts/${encodeURIComponent(id)}/share`, { method: "POST" }));
      const character = normalizeCharacterDraft(payload.character ?? payload);
      const shareToken = String(payload.shareToken ?? character.shareToken ?? "");
      const shareUrl = absoluteAppUrl(payload.shareUrl ?? character.shareUrl) ?? "";
      return { character: { ...character, shareToken, shareUrl }, shareToken, shareUrl };
    }, () => {
      const current = demoCharacterDrafts.get(id);
      if (!current) throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character not found.");
      const shareToken = crypto.randomUUID();
      const shareUrl = `${window.location.origin}/app/shared/character/${encodeURIComponent(id)}?share=${encodeURIComponent(shareToken)}`;
      const character = { ...current, visibility: "unlisted" as const, shareToken, shareUrl };
      demoCharacterDrafts.set(id, character);
      return { character, shareToken, shareUrl };
    });
  },
  editCharacterDraft(id: string): Promise<CharacterDraft> {
    return run(async () => normalizeCharacterDraft(await request<unknown>(`/characters/${encodeURIComponent(id)}/edit`, { method: "POST" })), () => demoCharacterDrafts.get(id) ?? (() => { throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character not found."); })());
  },
  copyCharacterDraft(id: string, shareToken?: string): Promise<CharacterDraft> {
    return run(async () => normalizeCharacterDraft(await request<unknown>(`/characters/${encodeURIComponent(id)}/copy`, { method: "POST", body: JSON.stringify(shareToken ? { shareToken } : {}) })), () => {
      const source = demoCharacterDrafts.get(id);
      if (!source) throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character not found.");
      const now = new Date().toISOString();
      const copy: CharacterDraft = { ...structuredClone(source), id: crypto.randomUUID(), status: "draft", revision: 0, visibility: "private", definition: { ...structuredClone(source.definition), name: `${source.definition.name} copy` }, createdAt: now, updatedAt: now };
      demoCharacterDrafts.set(copy.id, copy);
      return copy;
    });
  },
  deleteCharacter(id: string): Promise<void> {
    return run(
      () => request<void>(`/characters/${encodeURIComponent(id)}`, { method: "DELETE" }),
      () => {
        if (!demoCharacterDrafts.delete(id)) throw new ApiError(404, "CHARACTER_NOT_FOUND", "Character not found.");
      },
    );
  },
  gameDrafts(options: { scope?: "all" | "mine" | "public"; status?: "all" | "draft" | "ready"; search?: string } = {}): Promise<GameDraft[]> {
    const query = new URLSearchParams();
    if (options.scope) query.set("scope", options.scope);
    if (options.status) query.set("status", options.status);
    if (options.search?.trim()) query.set("search", options.search.trim());
    const suffix = query.size ? `?${query.toString()}` : "";
    return run(
      async () => {
        const payload = await request<unknown>(`/games/drafts${suffix}`);
        const envelope = record(payload);
        const items = Array.isArray(envelope.games) ? envelope.games : Array.isArray(envelope.drafts) ? envelope.drafts : Array.isArray(payload) ? payload : [];
        return items.map(normalizeGameDraft);
      },
      () => [...demoGameDrafts.values()].filter((item) => (!options.status || options.status === "all" || item.status === options.status) && (!options.search || item.definition.basics.title.toLowerCase().includes(options.search.toLowerCase()))),
    );
  },
  gameDraft(id: string): Promise<GameDraft> {
    return run(async () => normalizeGameDraft(await request<unknown>(`/games/drafts/${encodeURIComponent(id)}`)), () => demoGameDrafts.get(id) ?? (() => { throw new ApiError(404, "GAME_NOT_FOUND", "Campaign draft not found."); })());
  },
  sharedCampaign(id: string, shareToken?: string): Promise<SharedCampaignDetail> {
    const suffix = shareToken ? `?${new URLSearchParams({ share: shareToken }).toString()}` : "";
    return run(
      async () => normalizeSharedCampaign(await request<unknown>(`/games/${encodeURIComponent(id)}${suffix}`)),
      () => {
        const game = demoGameDrafts.get(id);
        if (!game) throw new ApiError(404, "GAME_NOT_FOUND", "Campaign not found.");
        return normalizeSharedCampaign(game);
      },
    );
  },
  createGameDraft(input: { definition: CampaignDraftDefinition; visibility?: CreationVisibility }): Promise<GameDraft> {
    return run(
      async () => normalizeGameDraft(await request<unknown>("/games/drafts", { method: "POST", body: JSON.stringify({ ...input, title: input.definition.basics.title }) })),
      () => {
        const now = new Date().toISOString();
        const draft: GameDraft = { id: crypto.randomUUID(), status: "draft", revision: 0, visibility: input.visibility ?? "private", definition: structuredClone(input.definition), createdAt: now, updatedAt: now };
        demoGameDrafts.set(draft.id, draft);
        return draft;
      },
    );
  },
  updateGameDraft(id: string, input: { expectedRevision: number; title?: string; visibility?: CreationVisibility; definition?: CampaignDraftDefinition }): Promise<GameDraft> {
    return run(
      async () => normalizeGameDraft(await request<unknown>(`/games/drafts/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(input) })),
      () => {
        const current = demoGameDrafts.get(id);
        if (!current) throw new ApiError(404, "GAME_NOT_FOUND", "Campaign draft not found.");
        if (current.revision !== input.expectedRevision) throw new ApiError(409, "STALE_DRAFT_REVISION", "This draft changed elsewhere.");
        const next = { ...current, ...(input.visibility ? { visibility: input.visibility } : {}), ...(input.definition ? { definition: structuredClone(input.definition) } : {}), revision: current.revision + 1, updatedAt: new Date().toISOString() };
        demoGameDrafts.set(id, next);
        return next;
      },
    );
  },
  finalizeGameDraft(id: string, expectedRevision: number): Promise<GameDraft> {
    return run(
      async () => normalizeGameDraft(await request<unknown>(`/games/drafts/${encodeURIComponent(id)}/finalize`, { method: "POST", body: JSON.stringify({ expectedRevision }) })),
      () => {
        const current = demoGameDrafts.get(id);
        if (!current) throw new ApiError(404, "GAME_NOT_FOUND", "Campaign draft not found.");
        const next = { ...current, status: "ready" as const, revision: current.revision + 1, updatedAt: new Date().toISOString() };
        demoGameDrafts.set(id, next);
        return next;
      },
    );
  },
  rotateGameShare(id: string): Promise<{ game: GameDraft; shareToken: string; shareUrl: string }> {
    return run(async () => {
      const payload = record(await request<unknown>(`/games/drafts/${encodeURIComponent(id)}/share`, { method: "POST" }));
      const game = normalizeGameDraft(payload.game ?? payload);
      const shareToken = String(payload.shareToken ?? game.shareToken ?? "");
      const shareUrl = absoluteAppUrl(payload.shareUrl ?? game.shareUrl) ?? "";
      return { game: { ...game, shareToken, shareUrl }, shareToken, shareUrl };
    }, () => {
      const current = demoGameDrafts.get(id);
      if (!current) throw new ApiError(404, "GAME_NOT_FOUND", "Campaign not found.");
      const shareToken = crypto.randomUUID();
      const shareUrl = `${window.location.origin}/app/shared/campaign/${encodeURIComponent(id)}?share=${encodeURIComponent(shareToken)}`;
      const game = { ...current, visibility: "unlisted" as const, shareToken, shareUrl };
      demoGameDrafts.set(id, game);
      return { game, shareToken, shareUrl };
    });
  },
  editGameDraft(id: string): Promise<GameDraft> {
    return run(async () => normalizeGameDraft(await request<unknown>(`/games/${encodeURIComponent(id)}/edit`, { method: "POST" })), () => demoGameDrafts.get(id) ?? (() => { throw new ApiError(404, "GAME_NOT_FOUND", "Campaign not found."); })());
  },
  copyGameDraft(id: string, shareToken?: string): Promise<GameDraft> {
    return run(async () => normalizeGameDraft(await request<unknown>(`/games/${encodeURIComponent(id)}/copy`, { method: "POST", body: JSON.stringify(shareToken ? { shareToken } : {}) })), () => {
      const source = demoGameDrafts.get(id);
      if (!source) throw new ApiError(404, "GAME_NOT_FOUND", "Campaign not found.");
      const now = new Date().toISOString();
      const copy: GameDraft = { ...structuredClone(source), id: crypto.randomUUID(), status: "draft", revision: 0, visibility: "private", definition: { ...structuredClone(source.definition), basics: { ...source.definition.basics, title: `${source.definition.basics.title} copy` } }, createdAt: now, updatedAt: now };
      demoGameDrafts.set(copy.id, copy);
      return copy;
    });
  },
  creatorAiQuote(input: CreatorAiRequest): Promise<CreatorAiQuote> {
    return run(async () => {
      const payload = await request<unknown>("/creator/ai/quote", { method: "POST", body: JSON.stringify(input) });
      return (record(payload).quote ?? payload) as CreatorAiQuote;
    }, () => ({ commandId: input.commandId, estimatedInputTokens: 350, estimatedOutputTokens: 650, maximumOutputTokens: 1_000, estimatedTotalTokens: 1_000, maximumTotalTokens: 1_350, model: "private text model", exact: false }));
  },
  creatorAiRun(input: CreatorAiRequest): Promise<CreatorAiRunResult> {
    return run(async () => {
      const payload = await request<unknown>("/creator/ai/run", { method: "POST", body: JSON.stringify(input) });
      return (record(payload).result ?? payload) as CreatorAiRunResult;
    }, () => ({
      patch: { targetPath: input.targetPath, operation: "replace", value: input.currentValue },
      usage: { inputTokens: 350, outputTokens: 1, costUsd: 0 },
      quote: { commandId: input.commandId, model: "private text model", estimatedInputTokens: 350, estimatedOutputTokens: 650, maximumOutputTokens: 1_000, estimatedTotalTokens: 1_000, maximumTotalTokens: 1_350, exact: false },
      repaired: false,
    }));
  },
  catalog(): Promise<CatalogGame[]> { return run(async () => (await request<{ games: RawGame[] }>("/games")).games.map(catalogAdapter), () => catalogGames); },
  personas(): Promise<PlayerPersona[]> { return run(async () => (await request<{ personas: PlayerPersona[] }>("/personas")).personas, () => [{ id: "persona-rowan", name: "Rowan Vale", adultAge: 28, pronouns: "they / them", appearance: "Observant with an understated style.", personality: "Measured, curious, and persistent.", boundaries: ["Adult characters only"], stats: { STR: 10, DEX: 14, CON: 13, INT: 15, WIS: 12, CHA: 8, FEM: 12, COR: 0 } }]); },
  createPersona(input: Omit<PlayerPersona, "id">): Promise<PlayerPersona> { return run(async () => (await request<{ persona: PlayerPersona }>("/personas", { method: "POST", body: JSON.stringify({ ...input, archived: false }) })).persona, () => ({ ...input, id: crypto.randomUUID() })); },
  startCampaign(gameVersionId: string, characterId: string, shareToken?: string): Promise<{ campaignId: string }> { return run(async () => { const result = await request<{ campaign: { id: string } }>("/campaigns", { method: "POST", body: JSON.stringify({ gameVersionId, characterId, ...(shareToken ? { shareToken } : {}) }) }); return { campaignId: result.campaign.id }; }, () => ({ campaignId: "campaign-ezekiel" })); },
  campaigns(): Promise<CampaignSummary[]> { return run(async () => { const items = (await request<{ campaigns: RawCampaignListItem[] }>("/campaigns")).campaigns; return Promise.all(items.map(async (item) => { try { const full = campaignAdapter(await request<Record<string, unknown>>(`/campaigns/${encodeURIComponent(item.id)}`)); return { id: full.id, gameId: full.gameId, title: full.title, chapter: full.chapter, location: full.location, turn: full.turn, updatedAt: new Date(item.updatedAt).toLocaleDateString(), progress: full.progress, stats: full.stats }; } catch { return campaignSummaryAdapter(item); } })); }, () => campaignSummaries); },
  campaign(id: string): Promise<Campaign> { return run(async () => campaignAdapter(await request<Record<string, unknown>>(`/campaigns/${encodeURIComponent(id)}`)), () => demoCampaign); },
  renameCampaign(id: string, title: string): Promise<void> { return run(async () => { await request(`/campaigns/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ title }) }); }, () => undefined); },
  deleteCampaign(id: string): Promise<void> { return run(() => request<void>(`/campaigns/${encodeURIComponent(id)}`, { method: "DELETE" }), () => undefined); },
  turnDiagnostics(turnId: string): Promise<AiTurnDiagnostics> { return request<AiTurnDiagnostics>(`/turns/${encodeURIComponent(turnId)}/diagnostics`); },
  libraries(): Promise<LibraryAsset[]> { return run(async () => (await request<{ assets: RawAsset[] }>("/libraries")).assets.map(libraryAdapter), () => libraryAssets); },
  async submitTurn(campaignId: string, action: string, expectedWorldVersion: number): Promise<{ turnId: string }> {
    return run(async () => { const result = await request<{ turn: { id: string } }>(`/campaigns/${encodeURIComponent(campaignId)}/turns`, { method: "POST", body: JSON.stringify({ action, expectedWorldVersion }), headers: { "Idempotency-Key": crypto.randomUUID() } }); return { turnId: result.turn.id }; }, async () => { await new Promise((resolve) => window.setTimeout(resolve, 700)); return { turnId: crypto.randomUUID() }; });
  },
  async regenerateTurn(turnId: string, expectedWorldVersion: number, guidance?: string): Promise<{ turnId: string }> {
    const trimmedGuidance = guidance?.trim();
    return run(
      async () => {
        const result = await request<{ turn: { id: string } }>(`/turns/${encodeURIComponent(turnId)}/regenerate`, {
          method: "POST",
          body: JSON.stringify({ expectedWorldVersion, ...(trimmedGuidance ? { guidance: trimmedGuidance } : {}) }),
          headers: { "Idempotency-Key": crypto.randomUUID() },
        });
        return { turnId: result.turn.id };
      },
      async () => { await new Promise((resolve) => window.setTimeout(resolve, 700)); return { turnId: crypto.randomUUID() }; },
    );
  },
  async rollTurn(turnId: string): Promise<{ roll: { natural: number; total: number; outcome: "critical_failure" | "failure" | "success" | "critical_success" } }> {
    return run(
      () => request(`/turns/${encodeURIComponent(turnId)}/roll`, { method: "POST" }),
      async () => ({ roll: { natural: 10, total: 10, outcome: "success" } }),
    );
  },
  async generateGame(prompt: string): Promise<{ draftId: string; title: string; draft: Record<string, unknown> }> {
    return run(async () => { const result = await request<{ draft: Record<string, unknown> }>("/games/generate", { method: "POST", body: JSON.stringify({ prompt }) }); return { draftId: crypto.randomUUID(), title: prompt.split(/\s+/).slice(0, 5).join(" ").replace(/[.,!?]+$/, "") || "Untitled generated world", draft: result.draft }; }, async () => { await new Promise((resolve) => window.setTimeout(resolve, 900)); return { draftId: crypto.randomUUID(), title: "Untitled generated world", draft: { premise: prompt, gmVoice: "Atmospheric and responsive", gamePrompt: "Run this world as a persistent tactical transformation RPG.", starterWorld: { maps: [], actors: [], objects: [] } } }; });
  },
};

function demoImageJob(kind: "generate" | "edit", input: { prompt: string; aspectRatio: ImageAspectRatio; resolution: ImageResolution; outputFormat?: ImageOutputFormat; sourceMediaId?: string }): ImageJob {
  const id = crypto.randomUUID();
  const mediaId = crypto.randomUUID();
  const format = input.outputFormat ?? "png";
  const size = input.resolution === "2K" ? 2048 : 1024;
  const job: ImageJob = {
    id,
    kind,
    model: kind === "edit" ? "qwen-image-edit" : "qwen-image",
    status: "succeeded",
    prompt: input.prompt,
    aspectRatio: input.aspectRatio,
    resolution: input.resolution,
    outputFormat: format,
    sourceMediaId: input.sourceMediaId ?? null,
    outputMediaId: mediaId,
    errorCode: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  demoImageJobs.set(id, job);
  demoMedia = [{ id: mediaId, mimeType: format === "jpeg" ? "image/jpeg" : format === "webp" ? "image/webp" : "image/png", width: size, height: size, byteSize: 524_288, createdAt: job.createdAt }, ...demoMedia];
  demoVenice = { ...demoVenice, balance: { ...demoVenice.balance, spendableCredits: Math.max(0, (demoVenice.balance.spendableCredits ?? 0) - (input.resolution === "2K" ? 9 : 5)) } };
  return job;
}
